// Attention Center — prioritized insight cards across a workspace.
//
// Deterministic, evidence-linked, never an "AI demo" panel. Every card
// explains why it's here and what to do about it.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Loader2, ShieldCheck, TriangleAlert, AlertCircle, Clock, MessageSquare,
  MailX, Eye, RotateCw, Zap, ArrowUpRight, Video, CalendarClock,
} from "lucide-react";

const SEVERITY_META = {
  critical: { ring: "border-rose-200",  bg: "bg-rose-50",   text: "text-rose-800",   Icon: AlertCircle,   label: "Critical" },
  high:     { ring: "border-rose-200",  bg: "bg-rose-50/60",text: "text-rose-800",   Icon: AlertCircle,   label: "High" },
  medium:   { ring: "border-amber-200", bg: "bg-amber-50",  text: "text-amber-900",  Icon: TriangleAlert, label: "Medium" },
  low:      { ring: "border-slate-200", bg: "bg-slate-50",  text: "text-slate-700",  Icon: Clock,         label: "Low" },
  info:     { ring: "border-slate-200", bg: "bg-white",     text: "text-slate-600",  Icon: Clock,         label: "Info" },
};

const TYPE_ICON = {
  "approval.latency":         Clock,
  "feedback.unresolved":      RotateCw,
  "comment.unanswered":       MessageSquare,
  "progress.stalled":         Zap,
  "portal.inactive":          Eye,
  "delivery.problem":         MailX,
  "client.expectation_gap":   TriangleAlert,
  "meeting.upcoming":         Video,
  "meeting.followup_missing": CalendarClock,
  "calendar.needs_review":    CalendarClock,
};

export default function AttentionCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/dashboard/intelligence")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const insights = data?.insights || [];
  const total = data?.total_active_signals || 0;

  return (
    <section data-testid="attention-center"
             className="mt-4 rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-end justify-between mb-5 gap-4">
        <div>
          <p className="overline mb-1">Attention center</p>
          <h2 className="font-display text-xl text-slate-900">
            What needs your attention today
          </h2>
          {!loading && (
            <p className="text-xs text-slate-400 mt-1">
              {total > 0
                ? `${total} active signal${total === 1 ? "" : "s"} across your clients · deterministic detectors, no AI guesses`
                : "All active clients are on track"}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div data-testid="attention-center-loading"
             className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning your workspace…
        </div>
      ) : insights.length === 0 ? (
        <div data-testid="attention-center-empty"
             className="text-center py-10">
          <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <p className="font-display text-lg text-slate-900">Everything looks on track</p>
          <p className="text-sm text-slate-500 mt-1">
            No pending signals across your active clients. We'll surface anything that needs a decision here.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {insights.slice(0, 8).map((c) => {
            const meta = SEVERITY_META[c.severity] || SEVERITY_META.info;
            const TypeIcon = TYPE_ICON[c.signal_type] || meta.Icon;
            return (
              <li key={c.card_id}
                  data-testid={`insight-card-${c.signal_type}`}
                  className={`rounded-lg border ${meta.ring} ${meta.bg} p-4 hover:shadow-sm transition-shadow cursor-pointer`}
                  onClick={() => navigate(c.recommended_action?.href || `/clients/${c.client_id}`)}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 border border-slate-200`}>
                    <TypeIcon className={`w-4 h-4 ${meta.text}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span data-testid={`insight-card-client-${c.client_id}`}
                            className="font-medium text-slate-900 truncate">{c.client_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${meta.ring} ${meta.text} bg-white`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.project_name}</p>
                    <p className="text-sm text-slate-800 mt-2 leading-snug">{c.why}</p>
                    <div className="flex items-center justify-between mt-3">
                      <button data-testid={`insight-card-cta-${c.card_id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(c.recommended_action?.href || `/clients/${c.client_id}`);
                              }}
                              className="text-xs text-slate-700 hover:text-[#C55A46] inline-flex items-center gap-1 font-medium">
                        {c.recommended_action?.label || "Open client"}
                        <ArrowUpRight className="w-3 h-3" />
                      </button>
                      {c.evidence_event_ids?.length > 0 && (
                        <span className="text-[10px] text-slate-400"
                              title={`Evidence: ${c.evidence_event_ids.length} event${c.evidence_event_ids.length === 1 ? "" : "s"}`}>
                          {c.evidence_event_ids.length} evidence
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
